import React, { useState, useEffect } from 'react';
import { getUploadAudit } from '../services/api';

const styles = {
  heading: { fontSize: '24px', fontWeight: '700', color: '#1a202c', marginBottom: '8px' },
  sub: { fontSize: '14px', color: '#718096', marginBottom: '20px' },
  bar: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  input: { padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '260px' },
  refreshBtn: { padding: '8px 16px', background: '#9C3D97', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' },
  count: { fontSize: '13px', color: '#718096' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  th: { padding: '10px 14px', textAlign: 'left', background: '#f7fafc', fontSize: '11px', fontWeight: '600', color: '#718096', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '10px 14px', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#4a5568' },
  badge: { padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700' },
  error: { background: '#fed7d7', color: '#c53030', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' },
};

const typeBadge = {
  grading_sheet: { bg: '#e9d8fd', color: '#6b46c1', label: 'Grading Sheet' },
  transcript: { bg: '#bee3f8', color: '#2b6cb0', label: 'Transcript' },
};

function formatAZ(isoString) {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) + ' AZ';
  } catch { return isoString; }
}

export default function UploadLogPage() {
  const [uploads, setUploads] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const result = await getUploadAudit();
      if (result.error) setError(result.error);
      else setUploads(result.uploads || []);
    } catch { setError('Failed to load the upload log.'); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? uploads.filter((u) =>
        (u.scenario_name || '').toLowerCase().includes(term) ||
        (u.uploaded_by_name || '').toLowerCase().includes(term) ||
        (u.uploaded_by || '').toLowerCase().includes(term))
    : uploads;

  return (
    <div>
      <h1 style={styles.heading}>Upload Log</h1>
      <p style={styles.sub}>A record of every case file upload (grading sheet and transcript) - who uploaded it and when. Newest first.</p>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.bar}>
        <input style={styles.input} placeholder="Search case or uploader..." value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <button style={styles.refreshBtn} onClick={load} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <span style={styles.count}>{filtered.length} upload{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Date / Time (AZ)</th>
            <th style={styles.th}>Case</th>
            <th style={styles.th}>File</th>
            <th style={styles.th}>Uploaded By</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((u, i) => {
            const tb = typeBadge[u.file_type] || { bg: '#edf2f7', color: '#4a5568', label: u.file_type };
            return (
              <tr key={i}>
                <td style={styles.td}>{formatAZ(u.uploaded_at)}</td>
                <td style={{ ...styles.td, fontWeight: '600' }}>{u.scenario_name || '(unnamed)'}</td>
                <td style={styles.td}>
                  <span style={{ ...styles.badge, background: tb.bg, color: tb.color }}>{tb.label}</span>
                </td>
                <td style={styles.td}>{u.uploaded_by_name || u.uploaded_by || '-'}</td>
              </tr>
            );
          })}
          {filtered.length === 0 && !loading && (
            <tr><td style={{ ...styles.td, textAlign: 'center', color: '#a0aec0' }} colSpan={4}>
              No uploads recorded yet.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
